<template>
  <div class="relative">
    <input ref="inputEl" v-model="q" placeholder="Find a grape…"
      class="w-44 md:w-56 px-3 py-1.5 rounded-full bg-card border border-line text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:border-wine/60"
      @focus="open = true" @blur="closeSoon"
      @keydown.escape="q = ''; open = false"
      @keydown.down.prevent="hi = Math.min(hi + 1, matches.length - 1)"
      @keydown.up.prevent="hi = Math.max(hi - 1, 0)"
      @keydown.enter.prevent="matches[hi] && pick(matches[hi])" />
    <div v-if="open && q.trim().length >= 2" class="absolute right-0 top-10 w-72 max-h-80 overflow-y-auto panel shadow-2xl z-50">
      <button v-for="(m, i) in matches" :key="m.grape.slug" @mousedown.prevent="pick(m)"
        class="w-full text-left px-3 py-2 text-sm flex items-center gap-2"
        :class="i === hi ? 'bg-wine/20' : 'hover:bg-wine/10'"
        @mousemove="hi = i">
        <span class="w-2 h-2 rounded-full shrink-0" :style="{ background: berryColor(m.grape.color) }" />
        <span>{{ m.grape.name }}</span>
        <span v-if="m.via" class="text-xs text-ink-faint truncate">a.k.a. {{ m.via }}</span>
      </button>
      <p v-if="!matches.length" class="px-3 py-2.5 text-xs text-ink-faint italic">
        No grape or synonym matches “{{ q.trim() }}”.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { fullGrapes, selectGrape, berryColor } from "../store"

const q = ref(""), open = ref(false), hi = ref(0)
const inputEl = ref(null)

watch(q, () => { hi.value = 0; open.value = true })

// Fold diacritics so "gruner" finds Grüner Veltliner, "mencia" finds Mencía.
const fold = s => Array.from(s.normalize("NFD")).filter(ch => { const c = ch.codePointAt(0); return c < 0x300 || c > 0x36f }).join("").toLowerCase()

// Bounded Levenshtein for typo tolerance ("nebiollo" → Nebbiolo).
function editDistance(a, b, cap) {
  if (Math.abs(a.length - b.length) > cap) return cap + 1
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    const cur = [i]
    let rowMin = i
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
      if (cur[j] < rowMin) rowMin = cur[j]
    }
    if (rowMin > cap) return cap + 1
    prev = cur
  }
  return prev[b.length]
}

// Score one candidate string against the query: lower is better, null is no match.
function scoreCandidate(cand, s) {
  const c = fold(cand)
  if (c === s) return 0
  if (c.startsWith(s)) return 1
  if (c.split(/[\s-]+/).some(w => w.startsWith(s))) return 2
  if (c.includes(s)) return 3
  if (s.length >= 4) {
    const cap = s.length >= 7 ? 2 : 1
    if (editDistance(c, s, cap) <= cap) return 4
    if (c.split(/[\s-]+/).some(w => w.length >= 4 && editDistance(w, s, cap) <= cap)) return 5
  }
  return null
}

const matches = computed(() => {
  const s = fold(q.value.trim())
  if (s.length < 2) return []
  const out = []
  for (const g of fullGrapes.value) {
    let best = scoreCandidate(g.name, s)
    let via = null
    for (const syn of g.synonyms || []) {
      const sc = scoreCandidate(syn, s)
      if (sc !== null && (best === null || sc + 0.5 < best)) { best = sc + 0.5; via = syn }
    }
    if (best !== null) out.push({ grape: g, score: best, via })
  }
  return out.sort((a, b) => a.score - b.score || a.grape.name.localeCompare(b.grape.name)).slice(0, 10)
})

function pick(m) {
  selectGrape(m.grape.slug)
  q.value = ""
  open.value = false
  inputEl.value?.blur()
}

function closeSoon() {
  setTimeout(() => (open.value = false), 150)
}
</script>
